import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Button, Modal, Loading, Input } from '@roo/roo';
import { getCookie } from '@utiljs/cookie';
import useToggle from '@/hooks/use-toggle';
import { useGetRequest } from '@/hooks/use-request';
import utils from '@/utils/utils';
import getEPassportUrl from '@/utils/epassport';
import ImageUploader from './ImageUploader';
import { ContractInfo, ContractStatus } from './types';
import './index.scss';
import { postDXAccount } from './api';

interface ProviderInfo extends urls.serviceProviderServiceProviderID_RESTful.res {
    renewalDeadline?: string;
    logoUrl: string;
}

const statusMap: Record<ContractStatus, string> = {
    [ContractStatus.VALID]: '有效',
    [ContractStatus.SUSPENDED]: '中止',
    [ContractStatus.TERMINATED]: '终止',
};

const modifyPasswordUrl = getEPassportUrl('edit-password', {
    continue: `${window.location.origin}/#/epassportcontinue?from=modifyPassword`,
});

const modifyPhoneUrl = getEPassportUrl('bind-phone');

const Account: React.FC = () => {
    const imgSrc = require('@/assets/images/schematic-diagram.png');
    const [DXAccount, changeDXAccount] = useState('');
    const [DXInputStatus, changeDDXInputStatus] = useState<'success' | 'error' | undefined>();
    const { data: providerInfo, loading: isProviderInfoLoading } = useGetRequest<
        {
            serviceProviderID: string;
            acctLogin: string;
        },
        ProviderInfo
    >('/api/serviceProvider');
    const { data: accountInfo, loading: isAccountInfoLoading } = useGetRequest<
        {},
        urls.serviceProviderAccount.res
    >('/api/serviceProvider/account');
    const { data: contractInfo, loading: isContractInfoLoading } = useGetRequest<{}, ContractInfo>(
        '/api/serviceProvider/contract/detail',
    );
    const {
        state: isModifyInfoModalVisible,
        open: openModifyInfoModal,
        close: closeModifyInfoModal,
    } = useToggle(false);

    const {
        state: isModifyPasswordModalVisible,
        open: openModifyPasswordModal,
        close: closeModifyPasswordModal,
    } = useToggle(false);

    const {
        state: isModifyPhoneModalVisible,
        open: openModifyPhoneModal,
        close: closeModifyPhoneModal,
    } = useToggle(false);

    const {
        state: isModifyDXAccountModalVisible,
        open: openModifyDXAccountModal,
        close: closeModifyDXAccountModal,
    } = useToggle(false);

    const {
        state: isModifyOperateModalVisible,
        open: oepnModifyOperateModal,
        close: closeModifyOperateModal,
    } = useToggle(false);

    const handleConfirmChangeDXAccount = () => {
        const rule = /^[0-9a-zA-Z_]+$/;
        if (!rule.test(DXAccount)) {
            changeDDXInputStatus('error');
            return;
        }
        postDXAccount({
            providerId: getCookie('serviceProviderID'),
            afterSaleDXAccount: DXAccount,
        }).then(() => {
            closeModifyDXAccountModal();
            utils.toast('success', '修改成功~');
            window.location.reload();
        });
    };

    const handleDXAccountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDXAccount = e.target.value;
        changeDDXInputStatus(undefined);
        changeDXAccount(newDXAccount);
    };

    const getChainAccountInfo = () => {
        if (!providerInfo?.accounts?.length) {
            return '';
        }
        return providerInfo?.accounts
            .filter(element => element.type === 1)
            .map(element => element.acctLogin)
            .join('、');
    };

    return (
        <div className="account-page-wrapper">
            <Loading
                visible={isProviderInfoLoading || isAccountInfoLoading || isContractInfoLoading}
            >
                <>
                    <div className="info-block">
                        <h2 className="info-block__title">账号信息</h2>
                        <div className="info-block__content">
                            <div className="info-block__field">
                                <span className="info-block__field-name">服务商后台账号</span>
                                <span className="info-block__field-value">
                                    {providerInfo?.account}
                                </span>
                                <Button type="brand-text" onClick={openModifyPasswordModal}>
                                    修改密码
                                </Button>
                            </div>
                            <div style={{}} className="info-block__chain-account">
                                <div className="info-block__field-account-name">商家端后台账号</div>
                                <div className="info-block__account-info">
                                    <span className="info-block__field-accounts">
                                        {getChainAccountInfo()}
                                    </span>
                                    <div className="info-block__tips">
                                        <span className="info-block__field-accounts">
                                            提示：如需修改密码，请前往商家端PC后台，点击“右上角账号-修改密码”
                                        </span>
                                        <Button
                                            className="info-block__tips-picture"
                                            onClick={oepnModifyOperateModal}
                                        >
                                            操作示意图
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">绑定手机</span>
                                {providerInfo?.phone && (
                                    <span className="info-block__field-value">
                                        {providerInfo?.phone}
                                    </span>
                                )}
                                <Button type="brand-text" onClick={openModifyPhoneModal}>
                                    {providerInfo?.phone ? '更换绑定手机' : '绑定手机'}
                                </Button>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">名称</span>
                                <span className="info-block__field-value">
                                    {providerInfo?.serviceProviderName}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">服务商ID</span>
                                <span className="info-block__field-value">
                                    {contractInfo?.providerId}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">地址</span>
                                <span className="info-block__field-value">
                                    {providerInfo?.address}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">售后处理人大象号</span>
                                <span className="info-block__field-value">
                                    {providerInfo?.afterSaleDXAccount}
                                </span>
                                <Button type="brand-text" onClick={openModifyDXAccountModal}>
                                    {providerInfo?.afterSaleDXAccount ? '更换大象号' : '绑定大象号'}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="info-block">
                        <div className="info-block__title">
                            <h2>账户信息</h2>
                            <Button type="brand-text" onClick={openModifyInfoModal}>
                                更改信息
                            </Button>
                        </div>
                        <div className="info-block__content">
                            <div className="info-block__field">
                                <span className="info-block__field-name">开户行</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.bankDetail}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">银行卡号</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.cardNum}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">户名</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.accountName}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">结算方式</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.settleType}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">最低结算金额</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.settleAmount}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">财务联系人</span>
                                <span className="info-block__field-value">
                                    {accountInfo?.principal}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="info-block">
                        <h2 className="info-block__title">企业信息</h2>
                        <div className="info-block__content">
                            <div className="info-block__field">
                                <span className="info-block__field-name">企业logo</span>
                                <div>
                                    {providerInfo && (
                                        <ImageUploader defaultUrl={providerInfo.logoUrl} />
                                    )}
                                    <div className="info-block__field-value icon-tip">
                                        注：企业logo会显示给买家，请上传代表您企业形象的图片
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="info-block">
                        <h2 className="info-block__title">合同信息</h2>
                        <div className="info-block__content">
                            <div className="info-block__field">
                                <span className="info-block__field-name">合同编号</span>
                                <span className="info-block__field-value">
                                    {contractInfo?.contractId}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">合同状态</span>
                                <span className="info-block__field-value">
                                    {contractInfo && statusMap[contractInfo.status]}
                                </span>
                                {providerInfo?.renewalDeadline && (
                                    <span>
                                        （请于{providerInfo.renewalDeadline}前完成合同续签）
                                    </span>
                                )}
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">合同开始日期</span>
                                <span className="info-block__field-value">
                                    {contractInfo &&
                                        utils.timestampToMinutes(contractInfo.startTime).date}
                                </span>
                            </div>
                            <div className="info-block__field">
                                <span className="info-block__field-name">合同结束日期</span>
                                <span className="info-block__field-value">
                                    {contractInfo &&
                                        utils.timestampToMinutes(contractInfo.endTime).date}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Modal
                        visible={isModifyInfoModalVisible}
                        onCancel={closeModifyInfoModal}
                        onConfirm={closeModifyInfoModal}
                        showCancelButton={false}
                        confirmText="我知道了"
                        lazy
                    >
                        如需修改账户信息，请描述您的需求发送至ODG.partner@meituan.com，工作人员会帮您修改
                    </Modal>

                    <Modal
                        className="passport-iframe modify-password-modal"
                        visible={isModifyPasswordModalVisible}
                        showConfirmButton={false}
                        showCancelButton={false}
                        onCancel={closeModifyPasswordModal}
                        lazy
                    >
                        <iframe src={modifyPasswordUrl}>
                            美团商家登录平台使用了框架技术，但是您的浏览器不支持框架，请升级您的浏览器以便正常访问。
                        </iframe>
                    </Modal>

                    <Modal
                        className="passport-iframe modify-phone-modal"
                        visible={isModifyPhoneModalVisible}
                        showConfirmButton={false}
                        showCancelButton={false}
                        onCancel={closeModifyPhoneModal}
                        lazy
                    >
                        <iframe src={modifyPhoneUrl}>
                            美团商家登录平台使用了框架技术，但是您的浏览器不支持框架，请升级您的浏览器以便正常访问。
                        </iframe>
                    </Modal>

                    <Modal
                        visible={isModifyDXAccountModalVisible}
                        onCancel={closeModifyDXAccountModal}
                        onConfirm={handleConfirmChangeDXAccount}
                        style={{ width: 600 }}
                        lazy
                    >
                        <div>
                            大象账号:
                            <Input
                                placeholder="请准确填写接收商家售后消息（退款/解约/签约）通知的大象号（如DX12345678）"
                                message="请输入字母、数字、下划线"
                                value={DXAccount}
                                status={DXInputStatus}
                                onChange={handleDXAccountInputChange}
                            />
                        </div>
                    </Modal>
                    <Modal
                        size="lg"
                        title="操作示意图"
                        showCancelButton={false}
                        visible={isModifyOperateModalVisible}
                        onConfirm={closeModifyOperateModal}
                        onCancel={closeModifyOperateModal}
                        confirmText="我知道了"
                        lazy
                    >
                        <span style={{ marginLeft: '38px' }}>
                            请在商家端PC后台，点击“右上角账号-修改密码”
                        </span>
                        <div style={{ textAlign: 'center', marginTop: '12px' }}>
                            <img alt="" src={imgSrc} />
                        </div>
                    </Modal>
                </>
            </Loading>
        </div>
    );
};

export default withRouter(Account);
